/**
 * 디바이스 정보 수집 훅
 * 문의하기 기능에서 자동 수집 정보를 제공합니다.
 */
import { useState, useEffect } from "react";
import { Capacitor } from "@capacitor/core";
import { Device } from "@capacitor/device";
import { Network } from "@capacitor/network";
import { App } from "@capacitor/app";
import { useLocation } from "react-router-dom";
import { useAuth } from "@/hooks/useAuth";
import { createLogger } from "@ict/shared";

const logger = createLogger({ module: "DeviceInfo" });

export interface DeviceMetadata {
  /** 현재 페이지 경로 */
  currentPath: string;
  /** 사용자 이름 */
  userName: string;
  /** 사용자 ID */
  userId: string;
  /** 회사명 */
  companyName: string;
  /** 플랫폼 (web, ios, android) */
  platform: string;
  /** 기기 모델 */
  model: string;
  /** 제조사 (Samsung, Apple 등) */
  manufacturer: string;
  /** OS 버전 */
  osVersion: string;
  /** 앱 버전 (versionName: 1.2.3) */
  appVersion: string;
  /** 앱 빌드번호 (versionCode) */
  appBuild: string;
  /** 앱 패키지 ID */
  appId: string;
  /** 네트워크 상태 */
  isOnline: boolean;
  /** 네트워크 타입 (wifi, cellular, none) */
  networkType: string;
  /** 배터리 잔량 (0~100%) */
  batteryLevel: number;
  /** 충전 중 여부 */
  isCharging: boolean;
  /** 언어 코드 (ko, en) */
  language: string;
  /** 화면 해상도 */
  screenResolution: string;
  /** 시뮬레이터/에뮬레이터 여부 */
  isVirtual: boolean;
}

export function useDeviceInfo(): DeviceMetadata {
  const location = useLocation();
  const { user } = useAuth();
  const [deviceInfo, setDeviceInfo] = useState({
    platform: Capacitor.getPlatform(),
    model: "",
    manufacturer: "",
    osVersion: "",
    appVersion: "",
    appBuild: "",
    appId: "",
    networkType: "unknown",
    batteryLevel: -1,
    isCharging: false,
    language: navigator.language?.slice(0, 2) || "unknown",
    isVirtual: false,
  });

  useEffect(() => {
    async function loadDeviceInfo() {
      try {
        const [info, appInfo, networkStatus, batteryInfo, langResult] = await Promise.all([
          Device.getInfo(),
          App.getInfo().catch(() => null),
          Network.getStatus().catch(() => null),
          Device.getBatteryInfo().catch(() => null),
          Device.getLanguageCode().catch(() => null),
        ]);

        setDeviceInfo({
          platform: info.platform,
          model: info.model || "unknown",
          manufacturer: info.manufacturer || "unknown",
          osVersion: info.osVersion || "unknown",
          appVersion: appInfo?.version || "unknown",
          appBuild: appInfo?.build || "unknown",
          appId: appInfo?.id || "unknown",
          networkType: networkStatus?.connectionType || "unknown",
          batteryLevel:
            batteryInfo?.batteryLevel != null ? Math.round(batteryInfo.batteryLevel * 100) : -1,
          isCharging: batteryInfo?.isCharging || false,
          language: langResult?.value || navigator.language?.slice(0, 2) || "unknown",
          isVirtual: info.isVirtual,
        });
      } catch (error) {
        logger.warn("디바이스 정보 조회 실패:", error);
      }
    }

    if (Capacitor.isNativePlatform()) {
      loadDeviceInfo();
    } else {
      // 웹 환경 폴백: navigator에서 기본 정보 추출
      const ua = navigator.userAgent;
      setDeviceInfo((prev) => ({
        ...prev,
        model: /Mobile|Android|iPhone/i.test(ua) ? "모바일 브라우저" : "데스크톱 브라우저",
        osVersion: ua.slice(0, 80),
        language: navigator.language?.slice(0, 2) || "unknown",
      }));
    }
  }, []);

  return {
    currentPath: location.pathname,
    userName: user?.name || "알 수 없음",
    userId: user?._id || "",
    companyName: user?.companyName || "알 수 없음",
    platform: deviceInfo.platform,
    model: deviceInfo.model,
    manufacturer: deviceInfo.manufacturer,
    osVersion: deviceInfo.osVersion,
    appVersion: deviceInfo.appVersion,
    appBuild: deviceInfo.appBuild,
    appId: deviceInfo.appId,
    isOnline: navigator.onLine,
    networkType: deviceInfo.networkType,
    batteryLevel: deviceInfo.batteryLevel,
    isCharging: deviceInfo.isCharging,
    language: deviceInfo.language,
    screenResolution: `${window.screen.width}x${window.screen.height}@${window.devicePixelRatio}x`,
    isVirtual: deviceInfo.isVirtual,
  };
}
